import { useMemo, useState } from "react";
import axios from "axios";
import { Search } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";
import { Panel, StatusBadge } from "@/components/admin/ui";

export default function SolutionOrganizationsTab({ organizations, solutionId, API, onSaved }) {
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [togglingId, setTogglingId] = useState(null);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return organizations;
    return organizations.filter((o) => o.name.toLowerCase().includes(q));
  }, [organizations, search]);

  const toggle = async (orgId, enabled) => {
    setTogglingId(orgId);
    setError("");
    try {
      await axios.patch(`${API}/admin/solutions/${solutionId}/organizations/${orgId}`, { enabled: !enabled }, { withCredentials: true });
      onSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <Panel title="Assigned Organizations" subtitle="Enable or disable this solution for individual organizations. Disabling does not delete any historical data.">
      <label className="relative mb-4 block max-w-sm">
        <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search organizations…" data-testid="solution-org-search" className="w-full rounded-xl border border-slate-300 py-2.5 pl-9 pr-4 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500" />
      </label>
      {error && <p className="mb-4 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="solution-org-error">{error}</p>}
      {filtered.length === 0 ? (
        <p className="text-sm text-slate-500 dark:text-slate-400" data-testid="solution-org-empty">No organizations found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[680px] text-left text-sm" data-testid="solution-organizations-table">
            <thead>
              <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.1em] text-slate-400 dark:border-white/10 dark:text-slate-500">
                <th className="py-3 pr-4 font-bold">Organization</th>
                <th className="py-3 pr-4 font-bold">Org Status</th>
                <th className="py-3 pr-4 font-bold">Owner</th>
                <th className="py-3 pr-4 font-bold">Users with Access</th>
                <th className="py-3 pr-4 font-bold">Solution Access</th>
                <th className="py-3 font-bold">Action</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.org_id} className="border-b border-slate-100 last:border-0 dark:border-white/5" data-testid={`solution-org-row-${o.org_id}`}>
                  <td className="py-3 pr-4 font-bold text-slate-950 dark:text-white">{o.name}</td>
                  <td className="py-3 pr-4"><StatusBadge status={o.status} testId={`solution-org-status-${o.org_id}`} /></td>
                  <td className="py-3 pr-4 text-slate-600 dark:text-slate-400">{o.owner_name || "—"}</td>
                  <td className="py-3 pr-4 text-slate-600 dark:text-slate-400">{o.users_with_access}</td>
                  <td className="py-3 pr-4">
                    <span className={`text-sm font-bold ${o.enabled ? "text-emerald-600 dark:text-emerald-400" : "text-slate-400 dark:text-slate-500"}`} data-testid={`solution-org-enabled-${o.org_id}`}>
                      {o.enabled ? "ENABLED" : "DISABLED"}
                    </span>
                  </td>
                  <td className="py-3">
                    <button
                      type="button"
                      onClick={() => toggle(o.org_id, o.enabled)}
                      disabled={togglingId === o.org_id}
                      data-testid={`solution-org-toggle-${o.org_id}`}
                      className={`rounded-lg border px-3 py-1.5 text-xs font-bold ${o.enabled ? "border-red-200 text-red-600 hover:bg-red-50 dark:border-red-500/30 dark:text-red-400 dark:hover:bg-red-500/10" : "border-emerald-200 text-emerald-600 hover:bg-emerald-50 dark:border-emerald-500/30 dark:text-emerald-400 dark:hover:bg-emerald-500/10"}`}
                    >
                      {o.enabled ? "Disable" : "Enable"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
}
